import { Dimensions } from 'react-native'
import Config from 'APP/Config'
import ColorLibrary from 'APP/Themes/ColorLibrary.json'
import { getStatusBarHeight, getBottomSpace } from 'react-native-iphone-x-helper'
import Constants from 'expo-constants'
import { Platform } from 'react-native'
import { css } from 'styled-components/native'

const type = Config.FONTS

/**
 * @deprecated Use SafeAreaView or useSafeAreaInsets instead
 */
const statusBarHeight = Platform.OS === 'ios' ? getStatusBarHeight() + 5 : Constants.statusBarHeight

/**
 * @deprecated Use SafeAreaView or useSafeAreaInsets instead
 */
const bottomSpace = getBottomSpace()

const { width, height } = Dimensions.get('window')

// Fonts
const fontTypes = {
  base: type.base,
  bold: type.bold,
  button: type.button,
  header: type.header,
  light: type.light,
}

const textTransform = {
  button: 'uppercase',
}

const fontSize = {
  h1: 30,
  h2: 24,
  h3: 20,
  h4: 16,

  large: 18,
  regular: 14,

  small: 12,
  captionBold: 11,
  caption: 10,
}

const lineHeight = {
  h1: 36,
  h2: 29,
  h3: 24,
  h4: 19,

  large: 20,
  regular: 16,
  small: 14,
  tinyBold: 13,
  tiny: 12,
}

const styles = {
  h1: {
    fontFamily: fontTypes.header,
    fontSize: fontSize.h1,
    lineHeight: lineHeight.h1,
  },
  h2: {
    fontFamily: fontTypes.header,
    fontSize: fontSize.h2,
    lineHeight: lineHeight.h2,
  },
  h3: {
    fontFamily: fontTypes.header,
    fontSize: fontSize.h3,
    lineHeight: lineHeight.h3,
  },
  h4: {
    fontFamily: fontTypes.header,
    fontSize: fontSize.h4,
    lineHeight: lineHeight.h4,
  },

  body: {
    fontFamily: fontTypes.light,
    fontSize: fontSize.regular,
    lineHeight: lineHeight.regular,
  },
  bodyLarge: {
    fontFamily: fontTypes.light,
    fontWeight: '300',
    fontSize: fontSize.large,
    lineHeight: lineHeight.large,
  },
  bodyLargeBold: {
    fontFamily: fontTypes.base,
    fontWeight: 'bold',
    fontSize: fontSize.large,
    lineHeight: lineHeight.large,
  },
  bodySmall: {
    fontFamily: fontTypes.light,
    fontWeight: '300',
    fontSize: fontSize.small,
    lineHeight: lineHeight.small,
  },
  bodySmallBold: {
    fontFamily: fontTypes.bold,
    fontWeight: '500',
    fontSize: fontSize.small,
    lineHeight: lineHeight.small,
  },

  button: {
    fontFamily: fontTypes.button,
    fontSize: fontSize.regular,
    letterSpacing: 1.5,
    lineHeight: lineHeight.regular,
    textTransform: 'uppercase',
  },

  caption: {
    fontFamily: fontTypes.base,
    fontSize: fontSize.caption,
    fontWeight: 'normal',
    lineHeight: lineHeight.tiny,
  },
  captionBold: {
    fontFamily: fontTypes.bold,
    fontSize: fontSize.captionBold,
    fontWeight: '500',
    lineHeight: lineHeight.tinyBold,
  },

  subheader: {
    fontFamily: fontTypes.base,
    fontSize: fontSize.regular,
    lineHeight: lineHeight.regular,
  },
  subheaderBold: {
    fontFamily: fontTypes.bold,
    fontSize: fontSize.regular,
    lineHeight: lineHeight.regular,
  },
}

// Shadows
const softShadow = css`
  shadow-color: #000000;
  shadow-offset: 0px 1px;
  shadow-opacity: 0.06;
  shadow-radius: 4px;
  elevation: 2;
`

const borderShadow = css`
  shadow-color: #000000;
  shadow-offset: 0px 0px;
  shadow-opacity: 0.2;
  shadow-radius: 1px;
`

const normalShadow = css`
  shadow-color: #000000;
  shadow-offset: 0px 3px;
  shadow-opacity: 0.16;
  shadow-radius: 5px;
  elevation: 2;
`

const heavyShadow = css`
  shadow-color: #000000;
  shadow-opacity: 0.85;
  shadow-radius: 12px;
`

export const shadows = {
  soft: softShadow,
  normal: normalShadow,
  heavy: heavyShadow,
  border: borderShadow,
}

const borderRadius = {
  small: 8,
  medium: 20,
  large: 30,
}

const metrics = {
  baseMargin: 16,
  // TODO: we should operate by components and avoid create constants for each component in theme
  button: {
    width: 200,
    height: 48,
    small: 44,
  },
  icon: {
    tiny: 15,
    xsmall: 20,
    small: 24,
    medium: 36,
    large: 48,
    xlarge: 56,
  },
  borderRadius,
  statusBarHeight,
  bottomSpace,
  screenWidth: width < height ? width : height,
  screenHeight: width < height ? height : width,
  web: {
    sidebarWidth: 240,
    contentMaxWidth: 800,
    postDescriptionMaxWidth: 500,
  },
  carouselDot: {
    size: 8,
    borderRadius: 4,
    margin: 6,
  },
}

export const baseColors = {
  background: ColorLibrary.snow,
  lightGray: ColorLibrary.smoke,
  white: ColorLibrary.white,
  error: ColorLibrary.alert,
  success: ColorLibrary.peppermint,
  packetLossWarning: ColorLibrary.pumpkin,
  packetLossCritical: ColorLibrary.alert,
  dim: ColorLibrary.dimGray,
  homeBannerBg: ColorLibrary.cornSilk,
  disabled: ColorLibrary.silver,
  primary: ColorLibrary.charcoal,
  secondary: ColorLibrary.charcoal,
  accent: ColorLibrary.pumpkin,
  selected: ColorLibrary.linen,
  gradientStart: ColorLibrary.peach,
  gradientEnd: ColorLibrary.sky,
  separateLine: ColorLibrary.smoke,
  elementsBg: ColorLibrary.white,
  elementsBorder: ColorLibrary.smoke,
  elementBgError: ColorLibrary.blush,
  contrastElementBg: ColorLibrary.smoke,
  switchTrackColor: ColorLibrary.silver,
  tagBg: ColorLibrary.sky,
  patientMsgBg: ColorLibrary.linen,
  practitionerMsgBg: ColorLibrary.smoke,
  todayHeaderActionIcon: ColorLibrary.dimGray,
  clientCardBg: ColorLibrary.linen,
  scaleTrackBg: ColorLibrary.white,
  serviceIconPrimary: ColorLibrary.charcoal,
  serviceIconAccent: ColorLibrary.pumpkin,
  videoBg: ColorLibrary.charcoal,
  bannerBg: ColorLibrary.sky,
  link: ColorLibrary.pumpkin,
  appointmentDateElement: ColorLibrary.linen,
  serviceDetailCardBg: ColorLibrary.linen,
  serviceDetailCardAccentBg: ColorLibrary.pumpkin,

  // Text Colours
  text: ColorLibrary.charcoal,
  textInverted: ColorLibrary.linen,
  textSecondary: ColorLibrary.dimGray,
  darkText: ColorLibrary.charcoal,
  primaryText: ColorLibrary.linen,
  primaryTextDisabled: ColorLibrary.darkSlate,
  secondaryText: ColorLibrary.dimGray,
  textDisabled: ColorLibrary.dimGray,
  errorText: ColorLibrary.alarm,
  serviceDetailCardText: ColorLibrary.pumpkin,
  serviceDetailCardAccentText: ColorLibrary.linen,
  buttonTextDisabled: ColorLibrary.cement,

  // Button Colours
  buttonPrimary: ColorLibrary.charcoal,
  buttonPrimaryDisabled: ColorLibrary.cement,
  buttonPrimarySelected: ColorLibrary.dimGray,
  buttonPrimaryText: ColorLibrary.linen,
  buttonPrimaryTextDisabled: ColorLibrary.snow,

  buttonSecondary: ColorLibrary.charcoal,
  buttonSecondaryText: ColorLibrary.charcoal,
  buttonSecondaryOutline: ColorLibrary.silver,
  buttonSecondaryDisabled: ColorLibrary.silver,
  buttonSecondaryBorderDisabled: ColorLibrary.silver,

  // Welcome Screen
  welcomeScreenSlideCurrentDot: ColorLibrary.pumpkin,
  welcomeScreenSlideInvertedDot: ColorLibrary.silver,
  welcomeScreenBackgroundSlide0: ColorLibrary.linen,
  welcomeScreenBackgroundSlide1: ColorLibrary.linen,
  welcomeScreenBackgroundSlide2: ColorLibrary.linen,
  welcomeScreenBackgroundSlide3: ColorLibrary.linen,

  // Slider
  sliderPrimary: ColorLibrary.darkSlate,
  sliderSecondary: ColorLibrary.linen,

  // TODO: Refactor/Deprecate
  buttonSecondarySolid: ColorLibrary.snow,
  buttonSecondaryActive: ColorLibrary.sky,
  buttonSelected: ColorLibrary.smoke,
  buttonLinen: ColorLibrary.linen,
  buttonLinenSelected: ColorLibrary.dirtyLinen,
  acceptButtonRipple: ColorLibrary.linen,
  buttonProfile: ColorLibrary.smoke,
}

const fonts = {
  fontTypes,
  fontSize,
  lineHeight,
  textTransform,
  styles,
}

export const baseTheme = {
  fonts,
  metrics,
  shadows,
  isSmallScreen: false,
  isBigScreen: false,
  insets: { top: 0, bottom: 0, left: 0, right: 0 },
}

export type BaseTheme = typeof baseTheme
